import React, { useState } from 'react'
import {
  RouteComponentProps,
  NavLink,
  Route,
  useRouteMatch,
} from 'react-router-dom'
import {
  Segment,
  Navbar,
  BannersDisplay,
  BannerPageWrapper,
  Flex,
} from '@sketch/components'
import {
  RouteParams,
  routes,
  useGetNewNotificationsCount,
} from '@sketch/modules-common'

import NotificationsListByType from 'modules/activity/components/Notifications/NotificationsListByType'
import { WorkspaceSidebarLayoutExtraProps } from 'modules/workspace/components/WorkspaceSidebarLayout/WorkspaceSidebarLayout'
import {
  useNotificationMarkAllAsReadMutation,
  GetNotificationsDocument,
  ParsedError,
  useGetNotificationsQuery,
  GetUserNotificationsCountDocument,
} from '@sketch/gql-types'
import {
  notificationMarkAllAsReadOptimisticResponse,
  getNotificationsQueryOptions,
} from '@sketch/user'
import {
  MarkAllAsReadButton,
  StyledMarkAsReadIcon,
  StyledSkeleton,
} from './Updates.styles'
import { NotificationsErrorDisplay } from 'modules/activity/components/Notifications/NotificationsErrorBoundary'
import { NotificationsPill } from 'modules/user/components/UserNavbarSection/UserDropdown/UserDropdown'
/**
 * TYPES
 */
export type UpdatesRoutesProps = RouteComponentProps<RouteParams<'UPDATES'>>

type UpdatesViewProps = UpdatesRoutesProps & WorkspaceSidebarLayoutExtraProps

/**
 * MAIN COMPONENT
 */
export const Updates: React.FC<UpdatesViewProps> = props => {
  const { NavbarPortal, useOverrideLayoutProps } = props

  const [error, setError] = useState<ParsedError | null>(null)

  const { data, loading } = useGetNotificationsQuery(
    getNotificationsQueryOptions
  )

  const { path } = useRouteMatch()

  const [markAllNotificationsAsRead] = useNotificationMarkAllAsReadMutation({
    redirectErrors: true,
    onError: setError,
    optimisticResponse: {
      __typename: 'RootMutationType',
      ...notificationMarkAllAsReadOptimisticResponse,
    },
    UNSAFE_ignoreResults: true,
    refetchQueries: [
      { query: GetNotificationsDocument },
      { query: GetUserNotificationsCountDocument },
    ],
    awaitRefetchQueries: true,
  })

  const unreadNotifications = useGetNewNotificationsCount({
    fetchPolicy: 'cache-only',
  })

  const hasUnreadNotifications = data?.me?.hasUnreadNotifications || false

  useOverrideLayoutProps({
    title: 'Updates',
  })

  const navbar = (
    <Navbar.Section ml={2}>
      <Segment
        type="text"
        label="Unread"
        render={(className, children) => (
          <NavLink
            exact
            className={className}
            to={routes.UPDATES_UNREAD.create({})}
          >
            {children}
            {hasUnreadNotifications && (
              <Flex pr={2} alignItems="center">
                <NotificationsPill unreadNotifications={unreadNotifications} />
              </Flex>
            )}
          </NavLink>
        )}
      />
      <Segment
        type="text"
        label="Read"
        render={(className, children) => (
          <NavLink
            exact
            className={className}
            to={routes.UPDATES_READ.create({})}
          >
            {children}
          </NavLink>
        )}
      />
      {path !== routes.UPDATES_READ.template() && (
        <Segment
          type="text"
          label="Mark all as read"
          render={(className, children) => {
            if (loading) {
              return <StyledSkeleton width="150px" height="32px" />
            }

            return (
              <MarkAllAsReadButton
                onClick={markAllNotificationsAsRead}
                disabled={!hasUnreadNotifications}
              >
                <StyledMarkAsReadIcon />
                Mark all as read
              </MarkAllAsReadButton>
            )
          }}
        />
      )}
    </Navbar.Section>
  )

  return (
    <>
      <NavbarPortal>{navbar}</NavbarPortal>

      <Route exact path={[routes.UPDATES_UNREAD.template()]}>
        {error ? (
          <NotificationsErrorDisplay />
        ) : (
          <>
            <BannerPageWrapper $hideMargins="top">
              <BannersDisplay />
            </BannerPageWrapper>
            <NotificationsListByType />
          </>
        )}
      </Route>
      <Route exact path={[routes.UPDATES_READ.template()]}>
        {/* Mounting the banner container to render them all */}
        <BannerPageWrapper $hideMargins="top">
          <BannersDisplay />
        </BannerPageWrapper>
        <NotificationsListByType />
      </Route>
    </>
  )
}
